/**
 * Fruit Slice в WebView: игра и отрисовка (Canvas 2D), поза — из
 * приложения. Единый код для приложений (`src/pages/fruit-slice-web/main.ts`,
 * `npm run build:fruit-slice-app`). Правила — `fruitSliceRules.ts`, перенос 1:1
 * из iOS; правки игры — только здесь.
 *
 * Приложение каждый кадр присылает кончики пальцев обоих игроков (0…1 поля,
 * y вниз, кадр уже зеркальный) и признак «игрок в своей рамке». Камера — под
 * прозрачной страницей, её рисует приложение. Звук — тоже приложение: сцена
 * шлёт события (`spawn`, `fuse`, `cut`, `blast`, `hit`).
 */
import {
  type FruitKind,
  type FruitMode,
  type Fruit,
  type Point,
  MODE_TUNING,
  PlayerState,
  RULES,
  isOutOfView,
  maxObjects,
  maxSpawnDelay,
  sliceFruit,
  spawnFruit,
  spawnFruitSolo,
  type Players,
  SwipeTracker,
  segmentCircleHitAt,
  sweepFruits,
  stepFruit,
} from './fruitSliceRules.ts';

export interface HandsFrame {
  /** Кончики пальцев: [игрок][рука], null — руки не видно. */
  readonly hands: readonly [readonly [Point | null, Point | null], readonly [Point | null, Point | null]];
  /** Игрок в своей рамке — только тогда его взмахи режут. */
  readonly active?: readonly [boolean, boolean];
  /** Метка времени кадра позы, мс (любые часы): по ней считается скорость взмаха; нет — часы страницы на приёме кадра. */
  readonly t?: number;
}

export type SoundEvent =
  | { readonly name: 'spawn' | 'cut' | 'blast' | 'hit' }
  | { readonly name: 'fuse' | 'fuseStop'; readonly id: number };

export interface FruitSliceStrings {
  /** «Комбо {n}» — `{n}` заменяется числом. */
  readonly combo?: string;
  readonly score?: string;
}

/** Состояние HUD одного игрока — его рисует приложение (нативные вью) или ТВ (свой HUD Fruit Slice). */
export interface PlayerHud {
  readonly score: number;
  readonly streak: number;
  readonly lives: number;
  readonly eliminated: boolean;
  /** Показанное комбо: число и точка поля 0…1 (y вниз). */
  readonly combo: { readonly count: number; readonly x: number; readonly y: number } | null;
}

export interface HudState {
  readonly players: readonly PlayerHud[];
  /** «На время» — секунд осталось, иначе null. */
  readonly timeLeft: number | null;
  /** Секунд игры — секундомер «Дзена». */
  readonly elapsed: number;
}

/** Вспышка половины (соло — всего экрана): потеря жизни — красная, бомба — белая, как в нативной игре. */
export type FlashKind = 'life' | 'bomb';

export interface FruitSliceOptions {
  readonly host: HTMLElement;
  readonly pixelRatio?: number;
  readonly images: Record<FruitKind | 'BOMB', { whole: string; half?: string }>;
  readonly strings?: FruitSliceStrings;
  /** Рисовать HUD в канве (стенд в браузере). В приложении и на ТВ HUD свой — `false`. */
  readonly drawHud?: boolean;
  /** Вспышки при потере жизни и бомбе — в канве, даже если HUD свой (ТВ: у его HUD вспышек нет). */
  readonly drawFlash?: boolean;
  readonly onHud?: (state: HudState) => void;
  readonly onFlash?: (player: 0 | 1, kind: FlashKind) => void;
  readonly onReady?: () => void;
  readonly onScore?: (scores: [number, number]) => void;
  readonly onFinished?: (scores: [number, number]) => void;
  readonly onSound?: (event: SoundEvent) => void;
  readonly onFps?: (fps: number) => void;
}

export interface FruitSliceGame {
  /** Режим и сколько игроков: 1 — соло на всё поле, 2 — по половине. */
  setMode(mode: FruitMode, players?: Players): void;
  setHands(frame: HandsFrame | null): void;
  start(): void;
  pause(): void;
  resume(): void;
  reset(): void;
  /** Продвинуть игру без часов — для скриншотов и тестов. */
  advance(seconds: number): void;
  destroy(): void;
}

/** Цвета нативных вью: `UIColor.systemCyan` / `.systemOrange` (след), `.cyan` / `.orange` 80 % (точки рук). */
const COLORS = {
  divider: 'rgba(255, 0, 0, 0.9)',
  trailCyan: '#32ade6',
  trailOrange: '#ff9500',
  dotCyan: 'rgba(0, 255, 255, 0.8)',
  dotOrange: 'rgba(255, 128, 0, 0.8)',
  panel: 'rgba(0, 0, 0, 0.8)',
  eliminated: 'rgba(0, 0, 0, 0.55)',
  heart: '#ff3b30',
};

/** Как `FruitSliceTrailView`: 10 точек на руку, линия 8, затухание к хвосту, всё стирается через 0,5 с после последней точки. */
const TRAIL = { maxPoints: 10, clearAfter: 0.5, width: 8 };
/** Точка руки — `GameHitDotView(side: 40)`. */
const HAND_DOT_RADIUS = 20;

function loadImage(src: string): HTMLImageElement {
  const image = new Image();
  image.src = src;
  // Декодируем заранее: иначе первый показ каждого фрукта декодирует картинку прямо в кадре и даёт задержку.
  image.decode?.().catch(() => undefined);
  return image;
}

export function createFruitSliceGame(options: FruitSliceOptions): FruitSliceGame {
  const canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%';
  options.host.appendChild(canvas);
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  const pixelRatio = options.pixelRatio ?? Math.min(devicePixelRatio, 2);

  const images = Object.fromEntries(Object.entries(options.images).map(([kind, set]) => [kind, {
    whole: loadImage(set.whole),
    half: set.half ? loadImage(set.half) : null,
  }])) as Record<FruitKind | 'BOMB', { whole: HTMLImageElement; half: HTMLImageElement | null }>;

  let width = 0;
  let height = 0;
  let mode: FruitMode = 'classic';
  let playerCount: Players = 2;
  let lastTrailAt = -Infinity;
  let lastHudKey = '';
  let lastHudAt = -Infinity;
  const players = [new PlayerState(), new PlayerState()];
  let fruits: Fruit[] = [];
  /** Фрукты, ушедшие за экран на этом шаге (один массив на все шаги). */
  const leaving: Fruit[] = [];
  const spawnAt: [number | null, number | null] = [null, null];
  let nextId = 1;
  let running = false;
  let paused = false;
  let finished = false;
  let gameTime = 0;
  let frame: HandsFrame | null = null;
  const swipes = [new SwipeTracker(), new SwipeTracker(), new SwipeTracker(), new SwipeTracker()];
  const trails: Point[][] = [[], [], [], []];
  const flashes: Array<{ alpha: number; kind: FlashKind }> = [{ alpha: 0, kind: 'life' }, { alpha: 0, kind: 'life' }];
  let frameId = 0;
  let drewLastFrame = true;
  let lastFrame = performance.now();
  let fpsFrames = 0;
  let fpsWindowStart = lastFrame;

  // один объект на все вызовы: поле меняется только в resize(), а физика спрашивает его на каждый фрукт каждого кадра
  const fieldSize = { width: 0, height: 0 };
  const field = () => fieldSize;
  const smallest = () => Math.min(width, height);
  const emitScore = () => options.onScore?.([players[0].score, players[1].score]);

  function resize(): void {
    width = options.host.clientWidth || window.innerWidth;
    height = options.host.clientHeight || window.innerHeight;
    fieldSize.width = width;
    fieldSize.height = height;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
  }

  /** Фитили несрезанных бомб гаснут: бомбы исчезают вместе с партией. */
  function stopFuses(): void {
    fruits.filter((f) => f.kind === 'BOMB' && !f.sliced)
      .forEach((f) => options.onSound?.({ name: 'fuseStop', id: f.id }));
  }

  function finish(): void {
    if (finished) return;
    finished = true;
    running = false;
    stopFuses();
    emitHud(true);
    // Партия кончилась: поле очищается (под меню итога не остаются летящие фрукты, следы и точки рук), как в нативной игре.
    fruits = [];
    trails.forEach((t) => { t.length = 0; });
    flashes.forEach((f) => { f.alpha = 0; });
    frame = null;
    options.onFinished?.([players[0].score, players[1].score]);
  }

  function eliminate(index: 0 | 1): void {
    const player = players[index];
    if (player.eliminated) return;
    player.eliminated = true;
    if (playerCount === 1 || (players[0].eliminated && players[1].eliminated)) finish();
  }

  function loseLife(index: 0 | 1): void {
    const player = players[index];
    player.streak = 0;
    if (mode !== 'classic') return;
    player.lives -= 1;
    flash(index, 'life');
    options.onSound?.({ name: 'hit' });
    if (player.lives <= 0) eliminate(index);
  }

  function onSlice(fruit: Fruit, angle: number, index: 0 | 1): void {
    const player = players[index];
    player.registerSlice(fruit, gameTime);
    sliceFruit(fruit, angle);
    if (fruit.kind !== 'BOMB') {
      player.addScore(1);
      options.onSound?.({ name: 'cut' });
      emitScore();
      return;
    }
    options.onSound?.({ name: 'fuseStop', id: fruit.id });
    options.onSound?.({ name: 'blast' });
    flash(index, 'bomb');
    if (mode === 'classic') {
      player.lives = 0;
      eliminate(index);
    } else if (mode === 'arcade') {
      player.addScore(RULES.bombPenaltyArcade);
      fruit.velocity.y = Math.max(fruit.velocity.y / 2, 100);
      emitScore();
    }
  }

  function flash(index: 0 | 1, kind: FlashKind): void {
    flashes[index] = { alpha: 0.5, kind };
    options.onFlash?.(index, kind);
  }

  /** След руки: взмах оставляет линию, через 0,5 с после последнего она стирается. */
  function addTrailPoint(index: 0 | 1, hand: 0 | 1, tip: Point): void {
    const trail = trails[index * 2 + hand];
    trail.push(tip);
    if (trail.length > TRAIL.maxPoints) trail.shift();
    lastTrailAt = gameTime;
  }

  /**
   * Взмах — движение руки быстрее порога по меткам времени кадров поз (как `SwipeTracker` нативной Android-игры),
   * один взмах режет все фрукты на пути, начиная с ближайших к началу отрезка, — как `FruitNinjaEngine.slice`.
   */
  function detectSwipe(index: 0 | 1, hand: 0 | 1, tip: Point | null, timeMs: number): void {
    const tracker = swipes[index * 2 + hand];
    if (!tip) {
      tracker.reset();
      return;
    }
    const minSpeed = smallest() * RULES.minSwipeSpeedPerSmallest;
    const maxJump = Math.max(width, height) * RULES.maxSwipeJumpPerBiggest;
    const isSwipe = tracker.update(tip.x, tip.y, timeMs, true, minSpeed, maxJump, RULES.swipeMaxSampleGapMs);
    if (!isSwipe) return;
    addTrailPoint(index, hand, tip);
    sliceAlong(index, { x: tracker.fromX, y: tracker.fromY }, tip);
  }

  function sliceAlong(index: 0 | 1, from: Point, to: Point): void {
    const reach = smallest() * RULES.handHitboxPercent;
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
    for (let left = fruits.length; left > 0 && running; left -= 1) {
      let nearest: Fruit | null = null;
      let nearestT = Infinity;
      for (let i = 0; i < fruits.length; i += 1) {
        const fruit = fruits[i];
        if (fruit.player !== index || fruit.sliced) continue;
        if (fruit.velocity.y < 0 && fruit.position.y > height * RULES.bottomExclusionRatio) continue;
        const t = segmentCircleHitAt(from, to, fruit.position, fruit.radius + reach);
        if (t >= 0 && t <= nearestT) {
          nearest = fruit;
          nearestT = t;
        }
      }
      if (!nearest) return;
      onSlice(nearest, angle, index);
    }
  }

  function applyHands(): void {
    if (!running || paused || !frame) return;
    const indices: ReadonlyArray<0 | 1> = playerCount === 1 ? [0] : [0, 1];
    indices.forEach((index) => {
      const active = frame?.active?.[index] ?? true;
      if (players[index].eliminated || !active) return;
      ([0, 1] as const).forEach((hand) => {
        const tip = frame?.hands[index][hand];
        const point = tip ? { x: tip.x * width, y: tip.y * height } : null;
        detectSwipe(index, hand, point, frame?.t ?? performance.now());
      });
    });
  }

  function spawnIfNeeded(index: 0 | 1): void {
    if (players[index].eliminated) return;
    let own = 0;
    for (let i = 0; i < fruits.length; i += 1) if (fruits[i].player === index) own += 1;
    if (own >= maxObjects(mode, gameTime, playerCount)) return;
    const at = spawnAt[index];
    if (at === null) {
      spawnAt[index] = gameTime + Math.random() * maxSpawnDelay(gameTime);
      return;
    }
    if (gameTime < at) return;
    spawnAt[index] = null;
    const fruit = playerCount === 1
      ? spawnFruitSolo(field(), mode, nextId, Math.random)
      : spawnFruit(field(), index, mode, nextId, Math.random);
    nextId += 1;
    fruits.push(fruit);
    options.onSound?.(fruit.kind === 'BOMB' ? { name: 'fuse', id: fruit.id } : { name: 'spawn' });
  }

  function step(dt: number): void {
    if (!running) return;
    const limit = MODE_TUNING[mode].timeLimit;
    gameTime += dt;
    if (limit !== null && gameTime >= limit) {
      gameTime = limit;
      finish();
      return;
    }
    players.forEach((player) => {
      if (player.commitCombo(gameTime)) emitScore();
    });
    (playerCount === 1 ? [0 as const] : [0 as const, 1 as const]).forEach(spawnIfNeeded);
    // На месте, без нового массива на каждый кадр; уход за экран обрабатывается после прохода: потеря последней
    // жизни кончает партию, а она очищает `fruits`.
    sweepFruits(fruits, (fruit) => {
      stepFruit(fruit, fieldSize, dt);
      return isOutOfView(fruit, fieldSize);
    }, leaving);
    for (let i = 0; i < leaving.length && running; i += 1) {
      const fruit = leaving[i];
      if (!fruit.sliced && fruit.kind !== 'BOMB') loseLife(fruit.player);
      if (fruit.kind === 'BOMB' && !fruit.sliced) options.onSound?.({ name: 'fuseStop', id: fruit.id });
    }
    // Вспышка гаснет за 0,2 с, как `UIView.animate(withDuration: 0.2)` в нативной игре.
    flashes.forEach((f) => { f.alpha = Math.max(0, f.alpha - dt * 2.5); });
    if (gameTime - lastTrailAt > TRAIL.clearAfter) trails.forEach((t) => { t.length = 0; });
    emitHud();
  }

  /** Состояние HUD — при изменении, но не чаще 15 раз в секунду; таймер — раз в секунду. */
  function emitHud(force = false): void {
    if (!options.onHud) return;
    // Не чаще 15 раз в секунду: состояние не собираем и не сериализуем на каждом кадре.
    if (!force && gameTime - lastHudAt < 1 / 15) return;
    const limit = MODE_TUNING[mode].timeLimit;
    const state: HudState = {
      players: players.slice(0, playerCount).map((p) => ({
        score: p.score,
        streak: p.streak,
        lives: p.lives,
        eliminated: p.eliminated,
        combo: p.combo ? { count: p.combo.count, x: p.combo.at.x / width, y: p.combo.at.y / height } : null,
      })),
      timeLeft: limit === null ? null : Math.max(0, Math.ceil(limit - gameTime)),
      elapsed: Math.floor(gameTime),
    };
    const key = JSON.stringify(state);
    if (!force && key === lastHudKey) return;
    lastHudKey = key;
    lastHudAt = gameTime;
    options.onHud(state);
  }

  // ─── Отрисовка ─────────────────────────────────────────────────────────────

  /**
   * Картинка шириной в диаметр, высота по пропорциям — как в нативной Android-игре (`drawSprite`: масштаб
   * `radius × 2 / bitmap.width`). Половинки не квадратные, в квадрат их растягивать нельзя. Поворот и сдвиг —
   * одной матрицей, без `save`/`restore`; после фруктов `render` возвращает базовую.
   */
  function drawImage(image: HTMLImageElement | null, at: Point, radius: number, degrees: number): void {
    if (!image || !image.complete || !image.naturalWidth) return;
    const angle = (degrees * Math.PI) / 180;
    const cos = Math.cos(angle) * pixelRatio;
    const sin = Math.sin(angle) * pixelRatio;
    ctx.setTransform(cos, sin, -sin, cos, at.x * pixelRatio, at.y * pixelRatio);
    const drawnHeight = (radius * 2 * image.naturalHeight) / image.naturalWidth;
    ctx.drawImage(image, -radius, -drawnHeight / 2, radius * 2, drawnHeight);
  }

  function drawFruit(fruit: Fruit): void {
    const set = images[fruit.kind];
    if (fruit.sliced && fruit.kind !== 'BOMB' && set.half) {
      drawImage(set.half, fruit.half1, fruit.radius, fruit.rotation1);
      drawImage(set.half, fruit.half2, fruit.radius, fruit.rotation2);
      return;
    }
    // Разрезанная бомба невидима, пока не упадёт за экран (в любом режиме) — как в нативной игре.
    if (fruit.sliced && fruit.kind === 'BOMB') return;
    drawImage(set.whole, fruit.position, fruit.radius, fruit.rotation);
  }

  /** След руки как `FruitSliceTrailView`: игрок 2 и правые руки — оранжевые, остальные — голубые. */
  function drawTrails(): void {
    ctx.lineCap = 'round';
    ctx.lineWidth = TRAIL.width;
    trails.forEach((trail, i) => {
      if (trail.length < 2) return;
      const isPlayer2 = i >= 2;
      const isRight = i % 2 === 1;
      ctx.strokeStyle = isPlayer2 || isRight ? COLORS.trailOrange : COLORS.trailCyan;
      for (let k = 0; k < trail.length - 1; k += 1) {
        ctx.globalAlpha = (k + 1) / trail.length;
        ctx.beginPath();
        ctx.moveTo(trail[k].x, trail[k].y);
        ctx.lineTo(trail[k + 1].x, trail[k + 1].y);
        ctx.stroke();
      }
    });
    ctx.globalAlpha = 1;
  }

  /** Точки рук как в нативной игре: соло — левая голубая, правая оранжевая; на двоих — игрок 1 оранжевый, 2 голубой. */
  function drawHands(): void {
    // Точки рук — только пока идёт партия: на паузе и после конца приложение уже не шлёт руки, а последние зависали
    if (!running || paused) return;
    frame?.hands.slice(0, playerCount).forEach((pair, index) => pair.forEach((tip, hand) => {
      if (!tip) return;
      if (playerCount === 1) ctx.fillStyle = hand === 0 ? COLORS.dotCyan : COLORS.dotOrange;
      else ctx.fillStyle = index === 0 ? COLORS.dotOrange : COLORS.dotCyan;
      ctx.beginPath();
      ctx.arc(tip.x * width, tip.y * height, HAND_DOT_RADIUS, 0, Math.PI * 2);
      ctx.fill();
    }));
  }

  function roundRect(x: number, y: number, w: number, h: number, r: number): void {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    ctx.fill();
  }

  /** Вспышка: соло — весь экран, на двоих — половина игрока. Красная — жизнь, белая — бомба. */
  function drawFlashes(): void {
    flashes.slice(0, playerCount).forEach((f, index) => {
      if (f.alpha <= 0) return;
      ctx.fillStyle = f.kind === 'bomb' ? `rgba(255,255,255,${f.alpha})` : `rgba(255,0,0,${f.alpha})`;
      ctx.fillRect(playerCount === 1 ? 0 : index * (width / 2), 0, playerCount === 1 ? width : width / 2, height);
    });
  }

  function drawHud(): void {
    if (options.drawHud === false || (!running && !finished)) return;
    ctx.fillStyle = COLORS.divider;
    ctx.fillRect(width / 2 - 4, 0, 8, height);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const boxW = 220;
    const boxH = 70;
    const boxX = width / 2 - boxW / 2;
    const boxY = height - 24 - boxH;
    ctx.fillStyle = COLORS.panel;
    roundRect(boxX, boxY, boxW, boxH, 20);
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(width / 2 - 1, boxY + 8, 2, boxH - 16);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 34px -apple-system, system-ui, sans-serif';
    ctx.fillText(String(players[0].score), boxX + boxW / 4, boxY + boxH / 2);
    ctx.fillText(String(players[1].score), boxX + (boxW * 3) / 4, boxY + boxH / 2);

    const limit = MODE_TUNING[mode].timeLimit;
    if (limit !== null) {
      const left = Math.max(0, Math.floor(limit - gameTime));
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      roundRect(width / 2 - 60, 24, 120, 50, 20);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 44px -apple-system, system-ui, sans-serif';
      const clock = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
      ctx.fillText(clock, width / 2, 50);
    }

    if (mode === 'classic') {
      ctx.font = '26px -apple-system, system-ui, sans-serif';
      ctx.fillStyle = COLORS.heart;
      ctx.textAlign = 'left';
      ctx.fillText('❤'.repeat(Math.max(0, players[0].lives)), 16, 32);
      ctx.textAlign = 'right';
      ctx.fillText('❤'.repeat(Math.max(0, players[1].lives)), width - 16, 32);
      ctx.textAlign = 'center';
    }

    players.forEach((player, index) => {
      const x0 = index * (width / 2);
      if (player.combo) {
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 40px -apple-system, system-ui, sans-serif';
        const text = (options.strings?.combo ?? 'Combo {n}').replace('{n}', String(player.combo.count));
        ctx.fillText(text, player.combo.at.x, player.combo.at.y);
      }
      if (player.eliminated) {
        ctx.fillStyle = COLORS.eliminated;
        ctx.fillRect(x0, 0, width / 2, height);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 72px -apple-system, system-ui, sans-serif';
        ctx.fillText(String(player.score), x0 + width / 4, height / 2 - 12);
        ctx.font = '500 20px -apple-system, system-ui, sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.8)';
        ctx.fillText(options.strings?.score ?? 'SCORE', x0 + width / 4, height / 2 + 36);
      }
    });
  }

  /** Есть ли что рисовать: пустой кадр после пустого не стираем и не рисуем. */
  function hasContent(): boolean {
    if (fruits.length > 0 || flashes.some((f) => f.alpha > 0) || trails.some((t) => t.length > 1)) return true;
    if (running && !paused && frame?.hands.slice(0, playerCount).some((pair) => pair.some(Boolean))) return true;
    return options.drawHud !== false && (running || finished);
  }

  function render(): void {
    const content = hasContent();
    if (!content && !drewLastFrame) return;
    drewLastFrame = content;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (!content) return;
    for (let i = 0; i < fruits.length; i += 1) drawFruit(fruits[i]);
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    drawTrails();
    drawHands();
    if (options.drawHud !== false || options.drawFlash) drawFlashes();
    drawHud();
  }

  function loop(now: number): void {
    frameId = requestAnimationFrame(loop);
    const dt = Math.min(0.05, (now - lastFrame) / 1000);
    lastFrame = now;
    if (!paused) step(dt);
    render();
    fpsFrames += 1;
    if (now - fpsWindowStart >= 1000) {
      options.onFps?.(Math.round((fpsFrames * 1000) / (now - fpsWindowStart)));
      fpsFrames = 0;
      fpsWindowStart = now;
    }
  }

  function reset(): void {
    running = false;
    paused = false;
    finished = false;
    gameTime = 0;
    stopFuses();
    fruits = [];
    spawnAt[0] = null;
    spawnAt[1] = null;
    players.forEach((p) => p.reset(mode));
    trails.forEach((t) => { t.length = 0; });
    flashes.forEach((f) => { f.alpha = 0; });
    frame = null;
    lastTrailAt = -Infinity;
    lastHudKey = '';
    swipes.forEach((tracker) => tracker.reset());
  }

  const onResize = () => resize();
  window.addEventListener('resize', onResize);
  resize();
  frameId = requestAnimationFrame(loop);
  queueMicrotask(() => options.onReady?.());

  return {
    setMode(next, count = 2) {
      mode = next;
      playerCount = count;
      players.forEach((p) => p.reset(mode));
    },
    setHands(next) {
      frame = next;
      applyHands();
    },
    start() {
      reset();
      running = true;
      emitScore();
      emitHud(true);
    },
    pause() {
      paused = true;
    },
    resume() {
      paused = false;
      lastFrame = performance.now();
    },
    reset,
    advance(seconds) {
      for (let left = seconds; left > 0; left -= 1 / 60) step(Math.min(1 / 60, left));
      render();
    },
    destroy() {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      canvas.remove();
    },
  };
}
